// src/api/wordbookGroupApi.ts
import { apiClient } from './axios';
import { WordbookGroup, WordbookGroupDetail } from '../types/wordbookGroup';
import {
    CreateWordbookGroupRequest,
    CreateWordbookGroupResponse,
} from '../models/wordbookGroup/createWordbookGroupRequest';
import { UpdateWordbookGroupRequest } from '../models/wordbookGroup/updateWordbookGroupRequest';
import { WorkbookGroupFilterParams } from '../models/wordbookGroup/wordbookGroupFilterParams';
import { PaginationData } from '../models/paginationData';
import {
    WorkbookGroupStatsResponse,
    WorkbookGroupOverviewStatsResponse,
    GroupLanguageStatsResponse,
    BulkDeleteWordbookGroupsResponse,
    BulkUpdateGroupVisibilityResponse,
} from '../models/wordbookGroup/wordbookGroupResponses';

export const wordbookGroupApi = {
    // 목록 조회
    getList: async (params: WorkbookGroupFilterParams) => {
        const { page, size, sortBy, sortDirection, ...filters } = params;

        return apiClient.get<PaginationData<WordbookGroup>>('/admin/wordbook-groups', {
            page: page - 1,
            size,
            sortBy: sortBy || 'createdAt',
            sortDirection: sortDirection || 'desc',
            ...filters,
        });
    },

    // 단건 조회
    getOne: async (id: number) => {
        return apiClient.get<WordbookGroupDetail>(`/admin/wordbook-groups/${id}`);
    },

    // 생성
    create: async (data: CreateWordbookGroupRequest) => {
        return apiClient.post<CreateWordbookGroupResponse>('/admin/wordbook-groups', data);
    },

    // 수정
    update: async (id: number, data: UpdateWordbookGroupRequest) => {
        return apiClient.putNoData(`/admin/wordbook-groups/${id}`, data);
    },

    // 삭제 (소프트 삭제)
    delete: async (id: number) => {
        return apiClient.deleteNoData(`/admin/wordbook-groups/${id}`);
    },

    // 영구 삭제
    permanentDelete: async (id: number) => {
        return apiClient.deleteNoData(`/admin/wordbook-groups/${id}/permanent`);
    },

    // 복원
    restore: async (id: number) => {
        return apiClient.postNoData(`/admin/wordbook-groups/${id}/restore`);
    },

    // 노출 토글
    toggleVisibility: async (id: number) => {
        return apiClient.patchNoData(`/admin/wordbook-groups/${id}/visibility`);
    },

    // 통계 조회
    getStats: async (id: number) => {
        return apiClient.get<WorkbookGroupStatsResponse>(`/admin/wordbook-groups/${id}/stats`);
    },

    // 일괄 삭제
    bulkDelete: async (groupIds: number[]) => {
        return apiClient.post<BulkDeleteWordbookGroupsResponse>(
            '/admin/wordbook-groups/bulk-delete',
            { groupIds }
        );
    },

    // 일괄 노출 설정
    bulkUpdateVisibility: async (groupIds: number[], visibility: string) => {
        return apiClient.post<BulkUpdateGroupVisibilityResponse>(
            '/admin/wordbook-groups/bulk-visibility',
            { groupIds, visibility }
        );
    },

    // 전체 통계
    getOverviewStats: async () => {
        return apiClient.get<WorkbookGroupOverviewStatsResponse>('/admin/wordbook-groups/stats/overview');
    },

    // 언어별 통계
    getLanguageStats: async () => {
        return apiClient.get<GroupLanguageStatsResponse>('/admin/wordbook-groups/stats/by-language');
    },
};
